import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  AiPredictionAsDatesUpdatedAtSchema,
  AiPreferencesSchema,
  // GetTimeIntervalPredictionsSchema,
  AiStatusLogSchema,
  AiWorkspaceDefaultsPatchSchema,
  AiWorkspaceDefaultsSchema,
  DateTimeTzSchema,
  GenericTimelyAccountId,
  GenericTimelyUserId,
  ResolvedWorkspacePreferencesSchema,
  TimesheetAssistantSchema,
} from '../../models';

extendZodWithOpenApi(z);
const c = initContract();

export const apiAiPreferencesRoute = c.router({
  getAiPreferences: {
    method: 'GET',
    path: `/1.0/predictions/preferences`,
    summary: 'Get AI preferences/settings',
    description: 'Returns the user preferences for the AI predictions, or default if none are set.',
    responses: {
      200: AiPreferencesSchema,
    },
  },
  saveAiPreferences: {
    method: 'POST',
    path: `/1.0/predictions/preferences`,
    summary: 'Save AI preferences (and if initial setup, set some defaults)',
    description:
      'Persists AI prediction preferences for the user. When called with the `initialize` side effect, triggers initial setup including default preference values and AI model initialization. Returns the saved preferences.',
    query: z.object({
      timely_account_id: GenericTimelyAccountId,
      side_effect: z
        .literal('initialize')
        .optional()
        .describe('Force re-initialization of AI model'),
    }),
    body: AiPreferencesSchema,
    responses: {
      200: AiPreferencesSchema,
    },
  },
  getRelevantUserDetailsForAiEngine: {
    method: 'GET',
    path: `/1.0/predictions/ai_user_details`,
    query: z.object({
      timely_user_id: GenericTimelyUserId,
      timely_account_id: GenericTimelyAccountId,
      range_from: DateTimeTzSchema.optional(),
      range_to: DateTimeTzSchema.optional(),
    }),
    summary: 'Relevant user settings, logs, etc for AI engine',
    description:
      'When `range_from/to` to is set, latest inference per day will also return. Replaces `getAiPreferencesForAiEngine` operation',
    responses: {
      200: z.object({
        preferences: AiPreferencesSchema,
        status: z.array(AiStatusLogSchema),
        dates_updated_at: AiPredictionAsDatesUpdatedAtSchema,
        model_reset_at: z.string().datetime({ offset: true }).nullable(),
      }),
    },
  },

  // Workspace-level timesheet assistant preferences
  getAiWorkspacePreferences: {
    method: 'GET',
    path: `/1.0/predictions/workspace_preferences`,
    summary: 'Get resolved workspace-level timesheet assistant preferences',
    description:
      'Returns the resolved timesheet assistant preferences for the current user in the specified workspace. Resolution: admin-enforced fields → user workspace settings → system defaults.',
    query: z.object({
      timely_account_id: GenericTimelyAccountId,
    }),
    responses: {
      200: ResolvedWorkspacePreferencesSchema,
    },
  },
  saveAiWorkspacePreferences: {
    method: 'POST',
    path: `/1.0/predictions/workspace_preferences`,
    summary: 'Save workspace-level timesheet assistant preferences',
    description:
      'Saves timesheet assistant preferences for the current user in the specified workspace. Returns 403 if attempting to change admin-locked fields.',
    query: z.object({
      timely_account_id: GenericTimelyAccountId,
    }),
    body: TimesheetAssistantSchema.partial(),
    responses: {
      200: ResolvedWorkspacePreferencesSchema,
      403: z.object({
        message: z.string(),
        locked_fields: z.array(z.string()),
      }),
    },
  },

  // Admin workspace defaults (enforced settings)
  getAiWorkspaceDefaults: {
    method: 'GET',
    path: `/1.0/predictions/workspace_defaults`,
    summary: 'Get admin-set workspace defaults',
    description:
      'Returns the admin-enforced defaults and locked fields for a workspace. Admins and owners get the full document; other members get it without the instruction text fields, plus has_note_instructions.',
    query: z.object({
      timely_account_id: GenericTimelyAccountId,
    }),
    responses: {
      200: AiWorkspaceDefaultsSchema,
    },
  },
  saveAiWorkspaceDefaults: {
    method: 'POST',
    path: `/1.0/predictions/workspace_defaults`,
    summary: 'Save admin workspace defaults (requires elevated role)',
    description:
      'Patches admin workspace defaults. Only owners and admins can use this endpoint. The body is merged over the saved document server-side: absent keys are kept, rule maps merge per rule id (null deletes a rule), and locked_fields replaces the enforced list only when present. Returns the saved document.',
    query: z.object({
      timely_account_id: GenericTimelyAccountId,
    }),
    body: AiWorkspaceDefaultsPatchSchema,
    responses: {
      200: AiWorkspaceDefaultsSchema,
      403: z.object({ message: z.string() }),
    },
  },
});
